import { z } from "zod";
import { nodeTypeValues } from "../schemas/nodeTypeSchema";
import { boardLayoutSchema, defaultBoardLayout } from "./boardConfig";
import { TABLE_COLUMN_TYPES, TABLE_VERSION } from "../lib/tableData";
import { videoImageRoles } from "../ia/videoModels";

const nodeTypeZodValidator = z.enum(nodeTypeValues);

type NodeVariant = {
  label: string;
  defaultWidth: number;
  defaultHeight: number;
  resizable?: boolean;
  isDefault?: boolean;
  /**
   * Passer à cette variante ne réécrit pas `width`/`height` du node.
   *
   * Par défaut, appliquer une variante remet le node à ses dimensions par
   * défaut. Une frame ne peut pas se le permettre : sa taille est celle du
   * contenu qu'elle englobe, la ramener à un gabarit ferait déborder ses
   * enfants. Ses variantes laissent donc la taille stockée intacte — la
   * compacte est rendue à sa taille fixe sans y toucher (cf.
   * `src/lib/frameVariant.ts`), et revenir à la dépliée retrouve la taille
   * d'avant.
   */
  preservesStoredSize?: boolean;
};

/**
 * Ce qu'un type de node autorise, au-delà de la forme de ses `values`.
 *
 * Ces comportements étaient jusqu'ici décidés par des `if (type === …)`
 * disséminés dans une douzaine de fichiers. Ils se déclarent désormais ici, et
 * chaque point d'application lit `getNodeCapabilities`.
 *
 * Tout vaut `true` par défaut : un type qui ne déclare rien se comporte
 * exactement comme avant l'introduction de ce bloc.
 */
type NodeCapabilities = {
  agent: {
    /**
     * Le TYPE est décrit à l'agent, dans le catalogue
     * `<available_node_types>` du system prompt.
     *
     * Distinct de `creatable` : un conteneur comme `frame` doit être compris
     * — l'agent va en croiser dans `nole ls` et dans la minimap — sans
     * pour autant être créable. Les deux étaient un seul flag jusqu'à
     * l'arrivée des frames, ce qui ne laissait le choix qu'entre « créable »
     * et « inconnu ».
     */
    exposed: boolean;
    /** Le TYPE figure dans l'enum des types que `create_node` accepte. */
    creatable: boolean;
    /**
     * Les INSTANCES lui sont visibles : `nole ls`, `nole read`, recherche
     * plein texte agent, minimap du canvas, contexte de message.
     */
    readable: boolean;
    /** L'agent peut écrire dessus : `set_node_data` et les tools dédiés. */
    writable: boolean;
  };
  /** Mentionnable par `@` dans un blocknote, et attachable au chat Nolë. */
  mentionable: boolean;
  /** Le contenu a un historique de versions (cf. lib/versionPolicy). */
  versioned: boolean;
  /**
   * Indexation de recherche : `embed: false` garde les chunks keyword
   * (`search_title` / `search_text`) mais saute la vectorisation Voyage
   * (pas d'`embedding`, exclu du vector search). Défaut `true`.
   */
  search: {
    embed: boolean;
  };
};

// Déclaration partielle : une entrée ne précise que ce qui s'écarte des
// défauts, `getNodeCapabilities` complète le reste.
type NodeCapabilitiesInput = Partial<
  Omit<NodeCapabilities, "agent" | "search">
> & {
  agent?: Partial<NodeCapabilities["agent"]>;
  search?: Partial<NodeCapabilities["search"]>;
};

const DEFAULT_NODE_CAPABILITIES: NodeCapabilities = {
  agent: { exposed: true, creatable: true, readable: true, writable: true },
  mentionable: true,
  versioned: true,
  search: { embed: true },
};

type NodeDataConfigItem = {
  type: z.infer<typeof nodeTypeZodValidator>;
  label: string;
  description: string;
  llmDescription: string;
  defaultDimensions: {
    width: number;
    height: number;
    resizable?: boolean;
  };
  variants?: Record<string, NodeVariant>;
  defaultColor?: string;
  capabilities?: NodeCapabilitiesInput;
  dataValuesSchema: z.ZodTypeAny;
  toolInputSchema?: z.ZodTypeAny; // Optional schema specifically for tool inputs, if different from dataValuesSchema
  /**
   * Les clés de `values` qu'une duplication de node NE recopie PAS.
   *
   * Dupliquer copie `values` en bloc (cf. `src/hooks/useDuplicateNode.ts`) mais
   * ne copie aucun edge : une value qui ne tient son sens que des connexions du
   * node d'origine n'a donc rien à faire sur le doublon. Elle y serait inerte,
   * et se rallumerait de façon surprenante si le doublon venait à être rebranché
   * sur les mêmes sources.
   *
   * Déclaré ici et pas dans le helper de duplication : c'est une propriété du
   * type de node, et `useDuplicateNode` doit rester générique.
   */
  valuesNotDuplicated?: string[];
};

/**
 * Dimensions par défaut des nouveaux nodes, centralisées ici — source unique
 * lue par le front (`prebuiltNodesConfig`, menu, raccourcis) comme par le
 * backend (`create_node` de l'agent). Ne s'appliquent qu'à la création (et au
 * changement explicite de variante) : jamais migrées sur les nodes existants.
 *
 * Trois largeurs : bandeaux une ligne, blocs carrés et médias, grands blocs.
 * Les hauteurs suivent l'usage du node.
 */
const baseWidth = 250;
const bigWidth = 352;
const extendedWidth = 400;
const titleVariantHeight = 40;
const shortBlockHeight = 132;
const audioPlayerHeight = 97;
const videoPlayerHeight = 255;
const boardHeight = 330;
const squareHeight = 352;

/**
 * La taille par défaut du titre d'une frame.
 *
 * Au milieu des trois niveaux : une frame est tracée autour de tout un groupe
 * de nodes, son titre nomme une section et doit se lire de loin. Les frames
 * déjà en base n'ont pas de `level` — c'est cette valeur qu'elles prennent, et
 * c'est voulu : leur titre passe de la taille d'une mention à celle d'un
 * intertitre, ce qui est exactement le but.
 */
const DEFAULT_FRAME_TITLE_LEVEL = "h2" as const;

/** Les tailles proposées pour le titre d'une frame, de la plus grande à la plus petite. */
const FRAME_TITLE_LEVELS = ["h1", "h2", "h3"] as const;

/**
 * Le titre d'un conteneur, frame ou board : le même, posé au-dessus de lui.
 * Même échelle que le node `title` : le canvas n'a qu'une hiérarchie de
 * titres, et un conteneur en est un niveau comme un autre. Sans le `p` de
 * `title` en revanche — il nomme une section, il n'a pas de raison de
 * descendre au corps de texte.
 */
const containerTitleShape = {
  title: z.string().default(""),
  level: z
    .enum(FRAME_TITLE_LEVELS)
    .describe("The size the title is drawn at.")
    .default(DEFAULT_FRAME_TITLE_LEVEL),
};

/**
 * La marge entre le contenu d'une frame et son bord.
 *
 * Une seule valeur pour trois usages qui doivent s'accorder au pixel : la borne
 * de rétrécissement au redimensionnement (côté client), la boîte que
 * `group_nodes` trace autour des nodes qu'il groupe, et la zone dans laquelle
 * `create_node({ frameId })` a le droit de poser. Si le client exigeait plus que
 * ce que le serveur laisse, l'utilisateur hériterait d'une frame dont le
 * plancher dépasse la taille — plus redimensionnable du tout.
 */
const FRAME_CONTENT_PADDING = 24;

/**
 * La frame compacte : une carte de taille fixe, qui remplace la frame et son
 * contenu sur le canvas. Ses enfants y restent rattachés, simplement masqués.
 */
const FRAME_COMPACT_VARIANT = "compact" as const;
const FRAME_COMPACT_WIDTH = 280;
const FRAME_COMPACT_HEIGHT = 64;

const nodeDataConfig: Array<NodeDataConfigItem> = [
  {
    type: "title",
    label: "Title",
    description:
      "Node for free text labels on the canvas. Supports h1/h2/h3/p heading levels for the whole text. Does not support rich markdown.",
    llmDescription:
      "Short standalone text: section headings, hubs, parents of related nodes (the branches of a tree of thought). For rich formatting (bold, lists…), use blocknote instead.",
    defaultDimensions: { width: baseWidth, height: titleVariantHeight, resizable: true },
    defaultColor: "transparent",
    // Titre court redondant : keyword suffit, pas de valeur sémantique.
    capabilities: {
      search: { embed: false },
    },
    dataValuesSchema: z
      .object({
        text: z.string().describe("The text content of the label.").default(""),
        level: z
          .enum(["h1", "h2", "h3", "p"])
          .describe("The heading level of the text.")
          .default("p"),
      })
      .default({ text: "", level: "p" }),
  },
  {
    type: "link",
    label: "Link",
    description: "Node for storing a link.",
    llmDescription:
      "A web link, shown as a one-line band, a preview card or an embedded page (YouTube, Google Docs/Sheets/Slides…). The display is the user's choice.",
    defaultDimensions: { width: baseWidth, height: titleVariantHeight, resizable: false },
    variants: {
      default: {
        label: "Default",
        defaultWidth: baseWidth,
        defaultHeight: titleVariantHeight,
        isDefault: true,
      },
      preview: {
        label: "Preview",
        defaultWidth: bigWidth,
        defaultHeight: shortBlockHeight,
      },
      // Le lien rendu dans une iframe, plutôt que décrit. Même value `href`
      // que les deux autres variantes : l'URL embarquable s'en dérive au rendu
      // (`convex/lib/embedUrl.ts`) et n'est jamais stockée, donc un lien devient
      // un embed par un simple changement de variante.
      embed: {
        label: "Embed",
        defaultWidth: extendedWidth,
        defaultHeight: squareHeight,
        resizable: true,
      },
    },

    dataValuesSchema: z
      .object({
        link: z
          .object({
            href: z
              .string()
              .describe(
                "The url of the link. You can visit the page using the open_web_page tool.",
              )
              .default(""),
            pageTitle: z
              .string()
              .describe("The title of the linked page.")
              .default(""),
          })
          .default({ href: "", pageTitle: "" }),
      })
      .default({ link: { href: "", pageTitle: "" } }),
    toolInputSchema: z
      .object({
        link: z
          .object({
            href: z.string().describe("The url of the link."),
            pageTitle: z.string().describe("The title of the linked page."),
            pageImage: z
              .string()
              .optional()
              .describe("Optional preview image URL for the linked page."),
            pageDescription: z
              .string()
              .optional()
              .describe("Optional description of the linked page."),
            siteName: z
              .string()
              .optional()
              .describe("Optional site name of the linked page."),
          })
          .strict(),
      })
      .strict(),
  },
  {
    type: "image",
    label: "Image",
    description: "Node for storing an image.",
    llmDescription:
      "One or more images, including ones you found or generated with other tools. Writing its 'imagePrompt' (the prompt of the node's generation tab) generates nothing: only the user starts a generation, from the node. The images of image nodes connected as inputs are attached as references, in edge order then image order, except those listed in 'imageExcludedReferenceUrls'. To point at one of them in the prompt, write a mention @[label](imageUrl), with the image's url from the input node: it is rewritten to \"image N\" (its position) when generating, and refused if that image is not sent.",
    defaultDimensions: { width: bigWidth, height: squareHeight, resizable: true },
    variants: {
      // Clé `default` et non `carousel` : les nodes image déjà en base portent
      // `variant: "default"`, posé à la création avant l'existence des variants.
      default: {
        label: "Carousel",
        defaultWidth: bigWidth,
        defaultHeight: squareHeight,
        resizable: true,
        isDefault: true,
      },
      grid: {
        label: "Grid",
        defaultWidth: bigWidth,
        defaultHeight: squareHeight,
        resizable: true,
      },
    },
    dataValuesSchema: z
      .object({
        // Nom donné par l'utilisateur. Absent ou vide : le titre retombe sur
        // le filename de la première image, puis sur « Image »
        // (cf. `getNodeDataTitle`).
        title: z
          .string()
          .optional()
          .describe("The title of the node, set by the user."),
        images: z
          .array(
            z.object({
              url: z
                .string()
                .describe(
                  "The URL of the image. nole read returns its indexed text description; " +
                    "to look at the image itself, use the view_image tool.",
                ),
            }),
          )
          .default([]),
        // Dernier prompt de génération, au niveau du node (un prompt par node,
        // pas par image). Écrasé à chaque génération, pas de versionning propre.
        imagePrompt: z
          .string()
          .optional()
          .describe(
            "The prompt the user generates images from. May contain image mentions @[label](imageUrl).",
          ),
        // Ancien interrupteur global, lu seulement tant que
        // `imageExcludedReferenceUrls` n'existe pas (cf.
        // readExcludedReferenceUrls). Plus jamais écrit.
        imageIncludeReferences: z
          .boolean()
          .optional()
          .describe(
            "Legacy: false excluded every input image. Ignored once imageExcludedReferenceUrls is set.",
          ),
        imageExcludedReferenceUrls: z
          .array(z.string())
          .optional()
          .describe(
            "Urls of input images unchecked by the user: not sent with the next generation. Every other input image is sent.",
          ),
      })
      .default({ images: [] }),
    toolInputSchema: z
      .object({
        // Tous optionnels : updateValues merge clé par clé, donc écrire l'un
        // seul préserve les autres.
        title: z
          .string()
          .optional()
          .describe(
            "The node's name. Empty: the node is named after its first image's filename.",
          ),
        images: z
          .array(
            z
              .object({
                url: z.string().describe("The URL of the image."),
              })
              .strict(),
          )
          .optional()
          .describe("The images to display on the node."),
        imagePrompt: z
          .string()
          .optional()
          .describe(
            "The image generation prompt stored on the node. Writing it does not start a generation. Mention an input image as @[label](imageUrl).",
          ),
        imageExcludedReferenceUrls: z
          .array(z.string())
          .optional()
          .describe(
            "Urls of input images NOT to send with the next generation. Every other input image is sent as a reference. Writing it replaces the legacy imageIncludeReferences=false switch: list every url to keep excluding all of them.",
          ),
      })
      .strict(),
  },
  {
    type: "blocknote",
    label: "Blocknote",
    description:
      "Node for storing a rich text document (BlockNote editor).",
    llmDescription:
      "A rich text document (headings, lists, tables, callouts, links, pills). Read it with nole read and edit it with the block tools (insert_blocks, replace_block, patch_block_text…), whose descriptions give the format.",
    defaultDimensions: { width: bigWidth, height: squareHeight, resizable: true },
    variants: {
      default: {
        label: "Preview",
        defaultWidth: bigWidth,
        defaultHeight: squareHeight,
        isDefault: true,
      },
      title: {
        label: "Title",
        defaultWidth: baseWidth,
        defaultHeight: titleVariantHeight,
        resizable: false,
      },
    },
    dataValuesSchema: z
      .object({
        doc: z.string().default("[]"),
      })
      .default({ doc: "[]" }),
    toolInputSchema: z.object({
      doc: z
        .string()
        .describe(
          "Plain Markdown content for a full document replace. Intentionally lossy: block ids are regenerated and block-level props (colors, alignment, etc.) are reset to defaults. Re-read the node before any block-id-addressed edit.",
        ),
    }),
  },
  {
    type: "value",
    label: "Value",
    description: "Node for storing a value (text, number, boolean).",
    llmDescription:
      "A single text, number or boolean value, with an optional unit and label, displayed like a KPI.",
    defaultDimensions: { width: baseWidth, height: shortBlockHeight, resizable: true },

    dataValuesSchema: z
      .object({
        value: z
          .object({
            type: z
              .enum(["text", "number", "boolean"])
              .describe(
                "The type of the value: 'text', 'number', or 'boolean'.",
              )
              .default("text"),
            value: z
              .union([z.string(), z.number(), z.boolean()])
              .describe("The actual value stored in the node.")
              .default(""),
            unit: z
              .string()
              .optional()
              .describe(
                "The unit of the value, if applicable (e.g., 'kg', 'm', etc.).",
              ),
            label: z
              .string()
              .optional()
              .describe("An optional label for the value."),
          })
          .default({ type: "text", value: "" }),
      })
      .default({ value: { type: "text", value: "" } }),
    toolInputSchema: z
      .object({
        value: z
          .object({
            type: z
              .enum(["text", "number", "boolean"])
              .describe(
                "The type of the value: 'text', 'number', or 'boolean'.",
              ),
            value: z
              .union([z.string(), z.number(), z.boolean()])
              .describe("The actual value stored in the node."),
            unit: z
              .string()
              .optional()
              .describe(
                "The unit of the value, if applicable (e.g., 'kg', 'm', etc.).",
              ),
            label: z
              .string()
              .optional()
              .describe("An optional label for the value."),
          })
          .strict(),
      })
      .strict(),
  },
  {
    type: "pdf",
    label: "PDF",
    description: "Node for storing uploaded PDF files.",
    llmDescription:
      "An uploaded PDF the user reads in Nolënor. nole read gives its table of contents, --pages its pages.",
    defaultDimensions: { width: baseWidth, height: titleVariantHeight, resizable: false },
    dataValuesSchema: z
      .object({
        files: z
          .array(
            z.object({
              url: z.string().describe("The public URL of the uploaded file."),
              filename: z.string().describe("The display filename."),
              mimeType: z.string().describe("The MIME type of the file."),
              size: z.number().describe("The file size in bytes."),
              uploadedAt: z
                .number()
                .describe("Upload timestamp (epoch milliseconds)."),
              key: z.string().describe("The storage key/path of the file."),
            }),
          )
          .default([]),
      })
      .default({ files: [] }),
  },
  {
    type: "table",
    label: "Table",
    description:
      `Node for structured tabular data with typed columns (${TABLE_COLUMN_TYPES.join(", ")}).`,
    llmDescription:
      `Structured data with typed columns (${TABLE_COLUMN_TYPES.join(", ")}). The user sees it through a shared view (sort, filters) that nole read and nole table apply, and table_set_view changes. Edit it with the table_* tools only.`,
    defaultDimensions: { width: extendedWidth, height: boardHeight, resizable: true },
    variants: {
      default: {
        label: "Preview",
        defaultWidth: extendedWidth,
        defaultHeight: boardHeight,
        isDefault: true,
      },
      title: {
        label: "Title",
        defaultWidth: baseWidth,
        defaultHeight: titleVariantHeight,
      },
    },

    dataValuesSchema: z
      .object({
        title: z.string().optional(),
        // Colonnes et cellules validées par le registre des champs à chaque
        // écriture (cf. `lib/tableOps.ts`) : ce schéma n'en fixe que la forme.
        table: z
          .object({
            version: z.literal(TABLE_VERSION).default(TABLE_VERSION),
            columns: z.array(z.record(z.string(), z.unknown())).default([]),
            rows: z
              .array(
                z.object({
                  id: z.string(),
                  cells: z.record(z.string(), z.unknown()),
                }),
              )
              .default([]),
          })
          .default({ version: TABLE_VERSION, columns: [], rows: [] }),
      })
      .default({ table: { version: TABLE_VERSION, columns: [], rows: [] } }),
  },
  {
    type: "database",
    label: "Database",
    description:
      "A collection of records typed by a template: each row has its own life (history, search, a record view).",
    llmDescription:
      "A collection of records sharing a template: each row is a record with typed fields, its own history and search entry. Its node shows all its rows, in their order; filtered, sorted or grouped views (boards, galleries, timelines) are separate databaseView nodes, read and filtered the same way. Edit it with the table_* tools only: columns are the template's fields, rows are records. Create one with create_node (optionally from a template of custom nodes), or turn a table into one with convert_table_to_database. Add views with create_database_view; show rows as nodes on the canvas, detach them, or add custom nodes as rows with database_row_nodes.",
    defaultDimensions: { width: extendedWidth, height: boardHeight, resizable: true },
    variants: {
      default: {
        label: "Preview",
        defaultWidth: extendedWidth,
        defaultHeight: boardHeight,
        isDefault: true,
      },
      title: {
        label: "Title",
        defaultWidth: baseWidth,
        defaultHeight: titleVariantHeight,
      },
    },
    capabilities: {
      // L'historique est celui de chaque ligne ; la vue et le titre n'en ont
      // pas besoin.
      versioned: false,
      // Le titre seul : les lignes sont indexées une par une.
      search: { embed: false },
    },
    dataValuesSchema: z
      .object({
        title: z.string().default(""),
        // Sa table (largeur et ordre des colonnes, hauteur des lignes),
        // relue par `lib/databaseView.readBaseView` ; ses autres vues sont
        // des nodes `databaseView`.
        views: z.array(z.record(z.string(), z.unknown())).default([]),
      })
      .default({ title: "", views: [] }),
  },
  {
    type: "databaseView",
    label: "Database view",
    description: "A view of a database on the canvas: a filtered table, a board, a gallery or a timeline.",
    llmDescription:
      "A view of a database (table, board, gallery or timeline) with its own filter, sort and shown fields. It holds no data: read and edit the rows through its database node. Created with create_database_view; edit_database_view changes its type, grouping, dates, hidden fields, filter and sort.",
    defaultDimensions: { width: extendedWidth, height: boardHeight, resizable: true },
    variants: {
      default: {
        label: "Preview",
        defaultWidth: extendedWidth,
        defaultHeight: boardHeight,
        isDefault: true,
      },
      title: {
        label: "Title",
        defaultWidth: baseWidth,
        defaultHeight: titleVariantHeight,
      },
    },
    capabilities: {
      // Créée depuis sa Database (`create_database_view`), pas par `create_node`.
      agent: { exposed: true, creatable: false },
      versioned: false,
      search: { embed: false },
    },
    dataValuesSchema: z
      .object({
        // Sa config (type, colonnes, filtre, tri, regroupement…), relue par
        // `lib/databaseView.readDatabaseView` ; son nom est son titre.
        view: z.record(z.string(), z.unknown()).default({}),
      })
      .default({ view: {} }),
  },
  {
    type: "app",
    label: "App",
    description:
      "Node for interactive React apps: prototypes, UI mockups, dashboards, charts, calculators...",
    llmDescription:
      "An interactive React mini-app (dashboard, chart, calculator…) running in a sandboxed iframe; through the nolenor SDK it reads, and can edit, the nodes connected to it (edges in either direction). Its 'title' is its name; its 'code' is a JSX string defining a component named App; its 'state' is free-form JSON the app persists.",
    defaultDimensions: { width: extendedWidth, height: boardHeight, resizable: true },
    variants: {
      preview: {
        label: "Preview",
        defaultWidth: extendedWidth,
        defaultHeight: boardHeight,
        resizable: true,
        isDefault: true,
      },
      title: {
        label: "Title",
        defaultWidth: baseWidth,
        defaultHeight: titleVariantHeight,
        resizable: false,
      },
    },
    capabilities: {
      // Titre seul : le code JSX n'a rien que l'utilisateur cherche (la
      // branche `app` de `chunkBuilder` n'indexe que le titre). Keyword
      // suffit, même raison que `frame`.
      search: { embed: false },
    },
    dataValuesSchema: z
      .object({
        // Le nom de l'app : sa barre de titre, `nole ls`, la recherche.
        title: z
          .string()
          .optional()
          .describe(
            'The app\'s name, shown in its title bar. Short and specific to what it does (e.g. "Tasks kanban"), never left empty or "App".',
          ),
        code: z.string().default(""),
        state: z.any().nullable().default(null),
        // Bumped automatically server-side when `code` changes. Used to
        // discard stale runtime errors posted by an iframe still running an
        // older version of the code.
        __v: z.string().optional(),
        // Captured from the iframe's runtime (window.onerror,
        // unhandledrejection, console.error, React ErrorBoundary).
        // Read-only for the LLM — it is reset to [] each time `code` changes.
        errors: z
          .array(
            z.object({
              type: z.string(),
              message: z.string(),
              stack: z.string().optional(),
              source: z.string().optional(),
              line: z.number().optional(),
              col: z.number().optional(),
              timestamp: z.number(),
            }),
          )
          .optional(),
      })
      .default({ code: "", state: null }),
  },
  {
    type: "audio",
    label: "Audio",
    description:
      "Node for playing an uploaded audio file, with a saved loop region.",
    llmDescription:
      "An audio file (music, recording, interview, voice memo). You can loop a passage the user describes ('loop', bounds in seconds). You cannot start a transcription; once the user has transcribed it, nole search finds passages with their timestamp and nole read returns the transcript (--range start:end, in seconds).",
    defaultDimensions: { width: bigWidth, height: audioPlayerHeight, resizable: true },
    variants: {
      player: {
        label: "Player",
        defaultWidth: bigWidth,
        defaultHeight: audioPlayerHeight,
        resizable: true,
        isDefault: true,
      },
      compact: {
        label: "Compact",
        defaultWidth: baseWidth,
        defaultHeight: titleVariantHeight,
        resizable: false,
      },
    },
    // Pas de `search.embed: false` : la transcription à la demande (chunks
    // `transcript`, cf. ia/transcriptionRun.ts) rend un passage parlé
    // retrouvable par concept, pas seulement par mot.
    dataValuesSchema: z
      .object({
        audio: z
          .object({
            url: z.string().describe("The public URL of the audio file."),
            filename: z.string().describe("The display filename."),
            mimeType: z.string().describe("The MIME type of the file."),
            size: z.number().describe("The file size in bytes."),
            uploadedAt: z
              .number()
              .describe("Upload timestamp (epoch milliseconds)."),
            key: z.string().describe("The storage key/path of the file."),
            duration: z
              .number()
              .describe("Duration of the audio in seconds.")
              .default(0),
            // Reserved for the waveform: normalised amplitude buckets (0-100).
            // Not populated yet — the node renders a plain progress bar.
            peaks: z.array(z.number()).default([]),
            title: z
              .string()
              .optional()
              .describe("Track title, read from the file's tags."),
            artist: z
              .string()
              .optional()
              .describe("Artist, read from the file's tags."),
            label: z
              .string()
              .optional()
              .describe(
                "Name given by the user, which overrides the tags and the filename.",
              ),
            cover: z
              .object({
                url: z.string().describe("Public URL of the cover art."),
                key: z.string().describe("Storage key of the cover art."),
              })
              .nullable()
              .default(null),
          })
          .nullable()
          .default(null),
        loop: z
          .object({
            start: z
              .number()
              .describe("Loop start, in seconds from the beginning.")
              .default(0),
            end: z
              .number()
              .describe("Loop end, in seconds from the beginning.")
              .default(0),
            enabled: z
              .boolean()
              .describe("Whether looping is active.")
              .default(false),
          })
          .default({ start: 0, end: 0, enabled: false }),
        playbackRate: z
          .number()
          .min(0.25)
          .max(4)
          .describe("Playback speed, 1 being normal.")
          .default(1),
      })
      .default({
        audio: null,
        loop: { start: 0, end: 0, enabled: false },
        playbackRate: 1,
      }),
    // `key` and `peaks` are deliberately absent: the storage key is owned by the
    // upload path, and peaks are derived data.
    toolInputSchema: z
      .object({
        audio: z
          .object({
            url: z.string().describe("The public URL of the audio file."),
            filename: z.string().describe("The display filename."),
            mimeType: z.string().describe("The MIME type of the file."),
            size: z.number().describe("The file size in bytes."),
            duration: z
              .number()
              .optional()
              .describe("Duration of the audio in seconds."),
            title: z
              .string()
              .optional()
              .describe("Track title, read from the file's tags."),
            artist: z
              .string()
              .optional()
              .describe("Artist, read from the file's tags."),
            label: z
              .string()
              .optional()
              .describe(
                "Name given by the user, which overrides the tags and the filename.",
              ),
          })
          .strict()
          .optional(),
        loop: z
          .object({
            start: z
              .number()
              .describe("Loop start, in seconds from the beginning."),
            end: z
              .number()
              .describe("Loop end, in seconds from the beginning."),
            enabled: z.boolean().describe("Whether looping is active."),
          })
          .strict()
          .optional(),
      })
      .strict(),
  },
  {
    type: "video",
    label: "Video",
    description: "Node for playing an uploaded video file.",
    llmDescription:
      "A video file the user plays on the canvas. You cannot watch it: you can reference, rename, move and link it, but never describe what it shows. Its 'videoPrompt' is the prompt of the node's generation tab: writing it generates nothing, only the user starts a generation (which replaces the video). Each image of the image nodes connected as inputs has a role in 'videoImageRoles': 'start' (exact opening frame), 'end' (exact closing frame), 'reference' (style/content guidance) or 'ignore'; frames and references cannot be combined, and images without a role default to start, then end, then ignore. Mention an input image in the prompt as @[label](imageUrl): it is rewritten to what the model receives (start/end frame, or \"image N\").",
    // 16/9 (225px de haut pour 400 de large, cf. `videoPlayerHeight`) plus
    // la ligne de titre. Les contrôles sont en surimpression sur l'image,
    // ils ne prennent pas de hauteur propre.
    defaultDimensions: { width: extendedWidth, height: videoPlayerHeight, resizable: true },
    variants: {
      player: {
        label: "Player",
        defaultWidth: extendedWidth,
        defaultHeight: videoPlayerHeight,
        resizable: true,
        isDefault: true,
      },
      title: {
        label: "Title",
        defaultWidth: baseWidth,
        defaultHeight: titleVariantHeight,
        resizable: false,
      },
    },
    // Filename seul (pas de transcription ni vision) : keyword suffit.
    capabilities: {
      search: { embed: false },
    },
    dataValuesSchema: z
      .object({
        video: z
          .object({
            url: z.string().describe("The public URL of the video file."),
            filename: z.string().describe("The display filename."),
            mimeType: z.string().describe("The MIME type of the file."),
            size: z.number().describe("The file size in bytes."),
            uploadedAt: z
              .number()
              .describe("Upload timestamp (epoch milliseconds)."),
            key: z.string().describe("The storage key/path of the file."),
            duration: z
              .number()
              .describe("Duration of the video in seconds.")
              .default(0),
            width: z
              .number()
              .describe("Frame width in pixels.")
              .default(0),
            height: z
              .number()
              .describe("Frame height in pixels.")
              .default(0),
            label: z
              .string()
              .optional()
              .describe(
                "Name given by the user, which overrides the filename.",
              ),
            // Still frame captured client-side at upload time and stored as a
            // second R2 object, like the audio node's cover art. Never inlined
            // as a data URI: it would bloat `values` and its version history.
            poster: z
              .object({
                url: z.string().describe("Public URL of the poster frame."),
                key: z.string().describe("Storage key of the poster frame."),
              })
              .nullable()
              .default(null),
            generatedBy: z
              .string()
              .optional()
              .describe(
                "Slug of the model that generated the video, absent for an upload.",
              ),
          })
          .nullable()
          .default(null),
        playbackRate: z
          .number()
          .min(0.25)
          .max(4)
          .describe("Playback speed, 1 being normal.")
          .default(1),
        videoPrompt: z
          .string()
          .optional()
          .describe(
            "The prompt the user generates the video from. May contain image mentions @[label](imageUrl).",
          ),
        // Absent ou incomplet : défaut par image, cf. resolveVideoImageRoles.
        videoImageRoles: z
          .array(
            z.object({
              url: z.string().describe("The url of an input image."),
              role: z.enum(videoImageRoles),
            }),
          )
          .optional()
          .describe(
            "The role of each input image in the next generation: 'start' (exact opening frame), 'end' (exact closing frame), 'reference' (style/content guidance) or 'ignore'. Frames and references cannot be combined. Images without a role default to start, then end, then ignore.",
          ),
      })
      .default({ video: null, playbackRate: 1 }),
    // `key` and `poster` are deliberately absent: both are owned by the upload
    // path, and the agent has no way to produce a valid one.
    toolInputSchema: z
      .object({
        video: z
          .object({
            url: z.string().describe("The public URL of the video file."),
            filename: z.string().describe("The display filename."),
            mimeType: z.string().describe("The MIME type of the file."),
            size: z.number().describe("The file size in bytes."),
            duration: z
              .number()
              .optional()
              .describe("Duration of the video in seconds."),
            width: z.number().optional().describe("Frame width in pixels."),
            height: z.number().optional().describe("Frame height in pixels."),
            label: z
              .string()
              .optional()
              .describe(
                "Name given by the user, which overrides the filename.",
              ),
          })
          .strict()
          .optional(),
        videoPrompt: z
          .string()
          .optional()
          .describe(
            "The video generation prompt stored on the node. Writing it does not start a generation. Mention an input image as @[label](imageUrl).",
          ),
        videoImageRoles: z
          .array(
            z
              .object({
                url: z.string().describe("The url of an input image."),
                role: z.enum(videoImageRoles),
              })
              .strict(),
          )
          .optional()
          .describe(
            "Role of each input image in the next generation: 'start', 'end', 'reference' or 'ignore'. Frames and references cannot be combined.",
          ),
      })
      .strict(),
  },
  {
    type: "frame",
    label: "Frame",
    description:
      "Container that groups nodes. Nodes inside a frame move with it and are addressable as a set.",
    llmDescription:
      "A container that groups nodes: moving it moves them all, and it is the canvas's explicit structure (nole ls --frame <id> lists its content). Create one with group_nodes around existing nodes; to put a new node in a frame, pass frameId to create_node. Nothing leaves a frame, and deleting a frame deletes its content.",
    // Grand gabarit : une frame est tracée autour de nodes existants, elle
    // part donc d'une taille qui en contient plusieurs. Ces dimensions ne
    // servent qu'aux frames créées sans tracé (aucune aujourd'hui) — l'outil
    // rectangle impose les siennes.
    defaultDimensions: { width: 600, height: 400, resizable: true },
    // Deux affichages d'une même frame. `defaultWidth`/`defaultHeight` ne
    // servent pas à la dépliée (sa taille est celle de son contenu, d'où
    // `preservesStoredSize`) ; ceux de la compacte sont sa taille de rendu.
    variants: {
      default: {
        label: "Expanded",
        defaultWidth: 600,
        defaultHeight: 400,
        resizable: true,
        isDefault: true,
        preservesStoredSize: true,
      },
      [FRAME_COMPACT_VARIANT]: {
        label: "Compact",
        defaultWidth: FRAME_COMPACT_WIDTH,
        defaultHeight: FRAME_COMPACT_HEIGHT,
        resizable: false,
        preservesStoredSize: true,
      },
    },
    capabilities: {
      agent: {
        // Décrite, et pas créable PAR `create_node` : l'agent en trace bien,
        // mais par `group_nodes`, autour de nodes qui existent déjà — une
        // frame vide posée par l'auto-placement ne grouperait rien. Ce flag ne
        // gate que l'enum de `create_node`, la `llmDescription` nomme la porte.
        exposed: true,
        creatable: false,
        readable: true,
        // `set_node_data` remplace `values` en bloc, et les values d'une frame
        // ne sont que son titre et la taille de celui-ci : lui ouvrir
        // l'écriture, c'est lui permettre de renommer silencieusement la
        // structure du canvas de l'utilisateur, sans contrepartie — il n'a
        // aucun contenu à y produire. Il nomme en revanche les frames qu'il
        // trace, au moment de les tracer : `group_nodes` exige un titre. Nommer
        // ce qu'on crée et renommer ce qu'un autre a créé ne sont pas le même
        // geste. À rouvrir quand la frame portera de l'automation.
        writable: false,
      },
      // Mentionnable : la pill se résout en `[[node:id|frame|Titre]]` et
      // l'agent suit avec `nole ls --frame` ou `nole read` pour lire le contenu groupé
      // (même contrat que l'attach Nolë, cf. `llmDescription` ci-dessus).
      // Défaut `true`, donc pas de ligne `mentionable` ici.
      // Un titre de conteneur n'a pas d'historique à remonter.
      versioned: false,
      // Titre seul : keyword suffit. Même raison que `title` — le contenu
      // d'une frame, ce sont les nodes qu'elle groupe, qui se vectorisent
      // chacun pour soi.
      search: { embed: false },
    },
    dataValuesSchema: z
      .object(containerTitleShape)
      .default({ title: "", level: DEFAULT_FRAME_TITLE_LEVEL }),
  },
  {
    type: "board",
    label: "Board",
    description:
      "Container split into spaces (columns, cells) that arrange the nodes dropped in them: a Kanban, a matrix.",
    llmDescription:
      "A container whose layout is a tree of rows and columns holding spaces, fixed text headings and dividers; each node inside sits in one space, stacked in order. It is how the user builds a Kanban (one space per column), a 2×2 matrix or any board where a node's place means something (a card in the 'Done' space is done). A space may have a name; column or axis headers are usually fixed texts of the layout. Use `nole read` on the board to see its layout tree, with ids, and the nodes of each space in order. \nCreate one with `create_board` (a grid of columns × rows, with optional text headers), and change it with `edit_board`: its whole layout (`layout`), a plain grid (`grid`), or which nodes sit where (`placements`). To add a NEW node, create it with create_node, then place it with edit_board — create_node's frameId does not accept a board. Like a frame, what enters a board stays in it, and deleting it deletes what it holds. \nLike a frame, it has a title shown above it (data values 'title' and 'level'). Its other data value is 'layout'.",
    defaultDimensions: { width: 960, height: 400, resizable: true },
    capabilities: {
      agent: {
        // Créé et rangé par ses tools dédiés (`create_board`, `edit_board`) :
        // `set_node_data` remplacerait l'arbre sans rien savoir des nodes
        // rangés dedans, et `create_node` ne sait pas poser un board.
        exposed: true,
        creatable: false,
        readable: true,
        writable: false,
      },
      // Comme la frame : une structure, pas un contenu.
      versioned: false,
      search: { embed: false },
    },
    dataValuesSchema: z
      .object({ ...containerTitleShape, layout: boardLayoutSchema })
      .default(() => ({
        title: "",
        level: DEFAULT_FRAME_TITLE_LEVEL,
        layout: defaultBoardLayout(),
      })),
  },
];

/**
 * Les conteneurs : des nodes dont d'autres nodes se déclarent enfants
 * (`parentId`). Ils partagent l'appartenance, sa cascade (supprimer le
 * conteneur supprime son contenu) et la règle d'un seul niveau — un
 * conteneur n'entre pas dans un autre.
 */
const CONTAINER_NODE_TYPES: readonly string[] = ["frame", "board"];

function isContainerNodeType(nodeType: string | undefined): boolean {
  return nodeType !== undefined && CONTAINER_NODE_TYPES.includes(nodeType);
}

function getDefaultNodeDataValues(
  nodeType: z.infer<typeof nodeTypeZodValidator>,
) {
  const config = nodeDataConfig.find((item) => item.type === nodeType);
  if (!config) {
    return null;
  }
  return config.dataValuesSchema.parse(undefined);
}

/**
 * Les capabilities effectives d'un type, défauts appliqués.
 *
 * Tolère un type inconnu de `nodeDataConfig` — `custom` n'y a pas d'entrée, sa
 * forme étant portée par un document `nodeTemplates` — et retombe alors sur les
 * défauts, c'est-à-dire sur le comportement d'un node ordinaire.
 */
function getNodeCapabilities(nodeType: string): NodeCapabilities {
  const declared = nodeDataConfig.find(
    (item) => item.type === nodeType,
  )?.capabilities;
  if (!declared) return DEFAULT_NODE_CAPABILITIES;

  return {
    agent: { ...DEFAULT_NODE_CAPABILITIES.agent, ...declared.agent },
    mentionable: declared.mentionable ?? DEFAULT_NODE_CAPABILITIES.mentionable,
    versioned: declared.versioned ?? DEFAULT_NODE_CAPABILITIES.versioned,
    search: { ...DEFAULT_NODE_CAPABILITIES.search, ...declared.search },
  };
}

/** Raccourci : l'agent n'a pas à connaître l'existence de ces nodes. */
function isNodeTypeReadableByAgent(nodeType: string): boolean {
  return getNodeCapabilities(nodeType).agent.readable;
}

/**
 * Raccourci : les chunks de ce type sont-ils vectorisés (Voyage) ?
 * `false` = keyword seul (`search_title` / `search_text`), pas d'`embedding`.
 */
function isNodeTypeEmbedded(nodeType: string): boolean {
  return getNodeCapabilities(nodeType).search.embed;
}

/**
 * `nodeTypeZodValidator` amputé des types que l'agent ne peut pas créer : le
 * JSON schema publié par `create_node` ne les liste donc pas, et une valeur
 * envoyée quand même est rejetée par zod.
 *
 * Filtre sur `creatable` et non sur `exposed` : un type peut être décrit à
 * l'agent sans lui être ouvert à la création (cf. `frame`).
 *
 * Le type inféré reste l'union complète — les branches par type en aval
 * continuent de typer normalement, seule la liste runtime est restreinte.
 */
const agentCreatableNodeTypeZodValidator = z.enum(
  nodeTypeValues.filter(
    (type) => getNodeCapabilities(type).agent.creatable,
  ) as unknown as [
    z.infer<typeof nodeTypeZodValidator>,
    ...Array<z.infer<typeof nodeTypeZodValidator>>,
  ],
);

export {
  isContainerNodeType,
  nodeDataConfig,
  nodeTypeZodValidator,
  DEFAULT_FRAME_TITLE_LEVEL,
  FRAME_TITLE_LEVELS,
  FRAME_CONTENT_PADDING,
  FRAME_COMPACT_VARIANT,
  FRAME_COMPACT_WIDTH,
  FRAME_COMPACT_HEIGHT,
  getDefaultNodeDataValues,
  getNodeCapabilities,
  isNodeTypeReadableByAgent,
  isNodeTypeEmbedded,
  agentCreatableNodeTypeZodValidator,
  DEFAULT_NODE_CAPABILITIES,
};
export type { NodeDataConfigItem, NodeVariant, NodeCapabilities };
export type FrameTitleLevel = (typeof FRAME_TITLE_LEVELS)[number];
